/**
 * 模版颜色映射 Hook
 * 
 * 根据当前模版返回对应的 Tailwind 颜色类名集合。
 * 所有可能的颜色类都在此文件中以字符串字面量形式出现，
 * 确保 Tailwind JIT 编译器能扫描到并生成所有类。
 */
import { useTemplate } from "@/context/TemplateContext";

export interface TemplateColorClasses {
  // 文字颜色
  text: string;
  textHover: string;
  textLight: string;
  textBrand: string;
  
  // 背景颜色
  bg: string;
  bgLight: string;
  bgDark: string;
  bgGradient: string;
  
  // 边框颜色
  border: string;
  borderHover: string;
  borderLight: string;
  borderActive: string;
  
  // 焦点/环
  focusBorder: string;
  focusRing: string;
  ring: string;
  
  // 渐变
  gradient: string;
  gradientLight: string;
  
  // 卡片悬停边缘
  cardHoverBorder: string;
  cardHoverText: string;
  
  // 徽章
  badgeBg: string;
  badgeText: string;
  
  // 占位图标
  placeholderIcon: string;

  // 单页/特殊
  headerGradient: string;
  textOnDark: string;
  linkText: string;
  linkHoverText: string;
  decoratorGradient: string;
  proseLink: string;
}

// 所有颜色映射 — Tailwind JIT 会扫描此文件并生成全部类名
const COLOR_MAPS: Record<string, TemplateColorClasses> = {
  classic: {
    text: "text-orange-600",
    textHover: "hover:text-orange-500",
    textLight: "text-orange-700",
    textBrand: "text-orange-500",
    bg: "bg-orange-600",
    bgLight: "bg-orange-50",
    bgDark: "bg-orange-700",
    bgGradient: "bg-gradient-to-br from-orange-600 to-orange-700",
    border: "border-orange-200",
    borderHover: "hover:border-orange-200",
    borderLight: "border-orange-300",
    borderActive: "border-orange-400",
    focusBorder: "focus:border-orange-400",
    focusRing: "focus:ring-orange-400",
    ring: "ring-orange-400",
    gradient: "from-orange-600 to-orange-700",
    gradientLight: "from-orange-100 to-orange-50",
    cardHoverBorder: "hover:border-orange-200",
    cardHoverText: "group-hover:text-orange-600",
    badgeBg: "bg-orange-600",
    badgeText: "text-orange-600",
    placeholderIcon: "text-orange-400",
    headerGradient: "from-orange-500 to-orange-700",
    textOnDark: "text-orange-200",
    linkText: "text-orange-600",
    linkHoverText: "hover:text-orange-700",
    decoratorGradient: "from-orange-300 to-yellow-300",
    proseLink: "prose-a:text-orange-600",
  },
  modern: {
    text: "text-blue-600",
    textHover: "hover:text-blue-500",
    textLight: "text-blue-700",
    textBrand: "text-blue-500",
    bg: "bg-blue-600",
    bgLight: "bg-blue-50",
    bgDark: "bg-blue-700",
    bgGradient: "bg-gradient-to-br from-blue-600 to-indigo-700",
    border: "border-blue-200",
    borderHover: "hover:border-blue-200",
    borderLight: "border-blue-300",
    borderActive: "border-blue-400",
    focusBorder: "focus:border-blue-400",
    focusRing: "focus:ring-blue-400",
    ring: "ring-blue-400",
    gradient: "from-blue-600 to-indigo-700",
    gradientLight: "from-blue-100 to-indigo-50",
    cardHoverBorder: "hover:border-blue-200",
    cardHoverText: "group-hover:text-blue-600",
    badgeBg: "bg-blue-600",
    badgeText: "text-blue-600",
    placeholderIcon: "text-blue-400",
    headerGradient: "from-blue-600 to-indigo-700",
    textOnDark: "text-blue-200",
    linkText: "text-blue-600",
    linkHoverText: "hover:text-blue-700",
    decoratorGradient: "from-cyan-300 to-blue-300",
    proseLink: "prose-a:text-blue-600",
  },
  dark: {
    text: "text-amber-400",
    textHover: "hover:text-amber-300",
    textLight: "text-amber-500",
    textBrand: "text-amber-400",
    bg: "bg-amber-500",
    bgLight: "bg-amber-500/10",
    bgDark: "bg-amber-600",
    bgGradient: "bg-gradient-to-br from-amber-500 to-yellow-600",
    border: "border-amber-500/30",
    borderHover: "hover:border-amber-500/50",
    borderLight: "border-amber-500/20",
    borderActive: "border-amber-500",
    focusBorder: "focus:border-amber-500",
    focusRing: "focus:ring-amber-500",
    ring: "ring-amber-500",
    gradient: "from-amber-500 to-yellow-600",
    gradientLight: "from-amber-500/10 to-yellow-600/5",
    cardHoverBorder: "hover:border-amber-500/30",
    cardHoverText: "group-hover:text-amber-400",
    badgeBg: "bg-amber-500",
    badgeText: "text-amber-400",
    placeholderIcon: "text-amber-500/50",
    headerGradient: "from-amber-500 to-yellow-600",
    textOnDark: "text-amber-400",
    linkText: "text-amber-400",
    linkHoverText: "hover:text-amber-300",
    decoratorGradient: "from-amber-400 to-yellow-400",
    proseLink: "prose-a:text-amber-400",
  },
  green: {
    text: "text-green-600",
    textHover: "hover:text-green-500",
    textLight: "text-green-700",
    textBrand: "text-green-500",
    bg: "bg-green-600",
    bgLight: "bg-green-50",
    bgDark: "bg-green-700",
    bgGradient: "bg-gradient-to-br from-green-600 to-emerald-700",
    border: "border-green-200",
    borderHover: "hover:border-green-200",
    borderLight: "border-green-300",
    borderActive: "border-green-400",
    focusBorder: "focus:border-green-400",
    focusRing: "focus:ring-green-400",
    ring: "ring-green-400",
    gradient: "from-green-600 to-emerald-700",
    gradientLight: "from-green-100 to-emerald-50",
    cardHoverBorder: "hover:border-green-200",
    cardHoverText: "group-hover:text-green-600",
    badgeBg: "bg-green-600",
    badgeText: "text-green-600",
    placeholderIcon: "text-green-400",
    headerGradient: "from-green-600 to-emerald-700",
    textOnDark: "text-green-200",
    linkText: "text-green-600",
    linkHoverText: "hover:text-green-700",
    decoratorGradient: "from-green-300 to-emerald-300",
    proseLink: "prose-a:text-green-600",
  },
  red: {
    text: "text-red-600",
    textHover: "hover:text-red-500",
    textLight: "text-red-700",
    textBrand: "text-red-500",
    bg: "bg-red-600",
    bgLight: "bg-red-50",
    bgDark: "bg-red-700",
    bgGradient: "bg-gradient-to-br from-red-600 to-rose-700",
    border: "border-red-200",
    borderHover: "hover:border-red-200",
    borderLight: "border-red-300",
    borderActive: "border-red-400",
    focusBorder: "focus:border-red-400",
    focusRing: "focus:ring-red-400",
    ring: "ring-red-400",
    gradient: "from-red-600 to-rose-700",
    gradientLight: "from-red-100 to-rose-50",
    cardHoverBorder: "hover:border-red-200",
    cardHoverText: "group-hover:text-red-600",
    badgeBg: "bg-red-600",
    badgeText: "text-red-600",
    placeholderIcon: "text-red-400",
    headerGradient: "from-red-600 to-rose-700",
    textOnDark: "text-red-200",
    linkText: "text-red-600",
    linkHoverText: "hover:text-red-700",
    decoratorGradient: "from-red-300 to-rose-300",
    proseLink: "prose-a:text-red-600",
  },
  teal: {
    text: "text-teal-600",
    textHover: "hover:text-teal-500",
    textLight: "text-teal-700",
    textBrand: "text-teal-500",
    bg: "bg-teal-600",
    bgLight: "bg-teal-50",
    bgDark: "bg-teal-700",
    bgGradient: "bg-gradient-to-br from-teal-600 to-cyan-700",
    border: "border-teal-200",
    borderHover: "hover:border-teal-200",
    borderLight: "border-teal-300",
    borderActive: "border-teal-400",
    focusBorder: "focus:border-teal-400",
    focusRing: "focus:ring-teal-400",
    ring: "ring-teal-400",
    gradient: "from-teal-600 to-cyan-700",
    gradientLight: "from-teal-100 to-cyan-50",
    cardHoverBorder: "hover:border-teal-200",
    cardHoverText: "group-hover:text-teal-600",
    badgeBg: "bg-teal-600",
    badgeText: "text-teal-600",
    placeholderIcon: "text-teal-400",
    headerGradient: "from-teal-600 to-cyan-700",
    textOnDark: "text-teal-200",
    linkText: "text-teal-600",
    linkHoverText: "hover:text-teal-700",
    decoratorGradient: "from-teal-300 to-cyan-300",
    proseLink: "prose-a:text-teal-600",
  },
};

/**
 * 根据当前模版返回对应的颜色类名集合
 */
export function useTemplateColors(): TemplateColorClasses {
  const { templateId } = useTemplate();
  return COLOR_MAPS[templateId] || COLOR_MAPS.classic;
}

export { COLOR_MAPS };
